<i18n locale="en" lang="json">
{
  "customerService": "Customer service"
}
</i18n>
<i18n locale="nl" lang="json">
{
  "customerService": "Klantenservice"
}
</i18n>

<template>
  <div class="d-print-none">
    <base-title-bar
      :tabs="tabs"
      :title="title"
      :back-location="backLocation"
      :loading="loadingOrCheckLogin"
      :test-id="testId"
      @pressed-tab="onClickTab"
    >
      <template #right>
        <template v-if="xs">
          <v-btn
            v-if="dienstverbandId"
            v-set-test-id="[componentName, 'showInfo']"
            icon
            @click="onClickContact"
          >
            <font-awesome-icon :icon="faHeadset" size="2x" />
          </v-btn>
        </template>
        <template v-else>
          <base-button
            v-if="dienstverbandId"
            color="white"
            :icon="faHeadset"
            test-id="customer-service"
            @click="onClickContact"
          >
            {{ t('customerService') }}
          </base-button>
        </template>
        <v-fade-transition v-if="!hideInfoButton && showInfo !== undefined">
          <v-btn
            v-show="!showInfo"
            v-set-test-id="[componentName, 'showInfo']"
            icon
            @click="$emit('update:show-info', !showInfo)"
          >
            <font-awesome-icon :icon="faCircleInfo" size="2x" />
          </v-btn>
        </v-fade-transition>
      </template>
    </base-title-bar>
  </div>

  <h2 v-if="showTitleOnPrint" class="d-none d-print-block my-6">{{ title }}</h2>

  <base-dialog
    v-if="dienstverbandId"
    v-model:is-open="contactDialogIsOpen"
    :title="t('customerService')"
    width="500"
    test-id="customer-service"
  >
    <contact-data :dienstverband-id="dienstverbandId" />
  </base-dialog>
</template>

<script lang="ts" setup>
import { faCircleInfo, faHeadset } from '@fortawesome/pro-regular-svg-icons';
import { computed, ref } from 'vue';
import { useDisplay } from 'vuetify';
import { useI18n } from 'vue-i18n';
import BaseTitleBar from '@/common/components/zvdzo/BaseTitleBar.vue';
import ContactData from '@/areas/contact/common/ContactData.vue';
import type { ZvdZLocationRaw } from '@/plugins/router';
import { previousRoute, router } from '@/plugins/router';
import { useDienstverbandIdFromRoute } from '@/common/composition/useDienstverbandId';
import useLoginData from '@/common/composition/useLoginData';
import type { ActionableComponentProps } from '@/common/components/ActionableComponent';
import { vSetTestId } from '@/common/components/ActionableComponent';

const componentName = 'TheTitleBar';

export type TabOptions = {
  id?: string;
  title: string;
  to?: ZvdZLocationRaw;
  exact?: boolean;
  target?: string;
  onClick?: () => void;
  disabled?: boolean;
  testId?: string;
};

type Props = ActionableComponentProps & {
  backButtonLocation?: ZvdZLocationRaw;
  title?: string;
  tabs?: TabOptions[];
  hideInfoButton?: boolean;
  showInfo?: boolean | undefined;
  loading?: boolean;
  showTitleOnPrint?: boolean;
};
const props = withDefaults(defineProps<Props>(), {
  showTitleOnPrint: true,
  showInfo: undefined,
});
type Emits = {
  (e: 'pressed-tab', t: TabOptions): void;
  (e: 'update:show-info', show: boolean): void;
};
const emit = defineEmits<Emits>();

const { loading: checkingLogin } = useLoginData();
const dienstverbandId = useDienstverbandIdFromRoute();
const contactDialogIsOpen = ref<boolean>(false);
const { t } = useI18n();

const loadingOrCheckLogin = computed(
  () => Boolean(props.loading) || Boolean(checkingLogin?.value),
);

const onClickContact = () => {
  contactDialogIsOpen.value = true;
};

const onClickTab = (item: TabOptions) => {
  if (item.onClick) {
    item.onClick();
  }
  emit('pressed-tab', item);
};

const backLocation = computed(() => {
  if (!props.backButtonLocation) {
    return undefined;
  }
  const route = router.resolve(props.backButtonLocation);
  if (!previousRoute.value || previousRoute.value.name !== route.name) {
    return route;
  }
  return previousRoute.value;
});

const { xs } = useDisplay();
</script>
